"use client";

/**
 * A confirmation step for actions that cannot be taken back.
 *
 * Replaces `window.confirm` at the moments where the consequence actually
 * matters — a vote that can never be changed, a checklist that locks for a
 * whole committee. The native dialog gives no room to say *why* the click is
 * irreversible, and it renders in the browser's chrome rather than the
 * product's, which reads as unfinished in a formal workflow.
 *
 * Built on the portal + backdrop pattern already working in
 * `components/gigs/RaiseHandModal.tsx` rather than `components/ui/dialog.tsx`,
 * which is a `@base-ui/react` wrapper with no importers anywhere in the app.
 *
 * The lock this provides is a courtesy to the user, never a control: the server
 * remains the only thing enforcing that a vote is once-only.
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

export interface ConfirmDialogProps {
  open: boolean;
  /** The question, phrased as the action. e.g. "Record your vote?" */
  title: string;
  /** One line on what cannot be undone. This is the reason the dialog exists. */
  consequence: string;
  confirmLabel: string;
  cancelLabel?: string;
  /** "danger" for the irreversible ones; the default reads as a normal action. */
  tone?: "default" | "danger";
  /** Keeps the dialog up, and both buttons inert, while the request is in flight. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function Spinner() {
  return (
    <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-t-transparent border-current" />
  );
}

export default function ConfirmDialog({
  open,
  title,
  consequence,
  confirmLabel,
  cancelLabel = "Cancel",
  tone = "default",
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  // Portals need a DOM; this component renders under the App Router, so guard
  // the first (server) pass.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // Escape cancels, but not mid-request — dismissing then would suggest the
      // action was called off when it is already on its way to the server.
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open || !mounted) return null;

  const confirmClass =
    tone === "danger"
      ? "bg-[#ae232d] hover:bg-[#8e1c24]"
      : "bg-[#007a8a] hover:bg-[#006574]";

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm font-sans animate-in fade-in duration-200"
      onClick={() => {
        if (!busy) onCancel();
      }}
      role="presentation"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl animate-in slide-in-from-bottom-2 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#003d45] px-5 py-4">
          <h2 className="text-[15px] font-black text-white">{title}</h2>
        </div>

        <div className="px-5 py-5">
          <p className="text-[13px] leading-relaxed text-gray-600">{consequence}</p>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-[13px] font-bold text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className={`rounded-lg px-3 py-1.5 text-[13px] font-bold text-white disabled:opacity-50 ${confirmClass}`}
          >
            {busy ? <Spinner /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
